/*
 * Copyright (c) 2024 Ask Sage, Inc. All rights reserved.
 * This software is the confidential and proprietary information of Ask Sage, Inc.
 * You shall not disclose such confidential information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Ask Sage, Inc.
 * THIS SOFTWARE IS PROVIDED "AS IS" AND ANY EXPRESSED OR IMPLIED WARRANTIES,
 * INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND
 * FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL ASK SAGE, INC.
 * OR ITS CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL,
 * EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT
 * OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS
 * INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT,
 * STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY
 * OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 * You may not use this software except in compliance with the License.
 * You may not modify, reverse engineer, or redistribute this software.
 */

import * as default_data from '../config.js'

const getFileExtension = (mimeType) => {
// Function to map a MIME type to a file extension
  const mimeToExtension = {
    'text/plain': '.txt',
    'application/msword': '.doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
    'application/vnd.oasis.opendocument.text': '.odt',
    'application/vnd.ms-powerpoint': '.ppt',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': '.pptx',
    'message/rfc822': '.eml',
    'application/vnd.ms-outlook': '.msg',
    'application/vnd.ms-excel': '.xls',
    'application/vnd.ms-excel.sheet.macroEnabled.12': '.xlsm',
    'text/html': '.html',
    'application/pdf': '.pdf',
    'text/xml': '.xml',
    'application/xml': '.xml',
    'application/csv': '.csv',
    'text/csv': '.csv',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
    'application/rtf': '.rtf',
    'application/epub+zip': '.epub',
    'application/zip': '.zip',
    'application/x-zip-compressed': '.zip',
    'application/json': '.json',
    'text/markdown': '.md',
    'image/jpeg': '.jpeg',
    'image/jpg': '.jpg',
    'image/png': '.png',
    'text/tab-separated-values': '.tsv',
    'text/x-c': '.c',
    'text/x-h': '.h',
    'application/x-httpd-php': '.php',
    'application/javascript': '.js',
    'text/x-python': '.py',
    'text/x-python-script': '.py',
    'audio/mpeg': '.mp3',
    'video/mp4': '.mp4',
    'video/mpeg': '.mpeg',
    'audio/mp4': '.m4a',
    'audio/x-m4a': '.m4a',
    'audio/wav': '.wav',
    'video/webm': '.webm',
    'application/sql': '.sql',
    'text/x-csharp': '.cs',
    'application/x-yaml': '.yaml',
    'application/yaml': '.yaml',
    'text/yaml': '.yaml',
    'text/x-java-source': '.java',
    'application/x-ruby': '.rb',
    'text/x-ruby-script': '.rb',
    'application/x-sh': '.sh',
    'application/x-bat': '.bat',
    'application/x-powershell': '.ps1',
    'application/wsdl+xml': '.wsdl',
    'application/xsd+xml': '.xsd',
    'text/x-sh': '.sh',
    'text/javascript': '.js',
    'text/php': '.php',
    'text/rtf': '.rtf',
    'text/x-scala': '.scala',
    'text/x-sbt': '.sbt',
    'text/x-go': '.go',
    'text/x-c++src': '.cpp',
    'text/x-c++hdr': '.h',
    'application/x-ipynb+json': '.ipynb',
    'text/x-dockerfile': '.dockerfile',
    'application/typescript': '.ts',
    'text/typescript': '.ts',
    'application/typescript-jsx': '.tsx',
    'text/typescript-jsx': '.tsx',
    'text/x-rustsrc': '.rs' // Added for .rs
  };

  // Return the file extension corresponding to the input MIME type
  return mimeToExtension[mimeType];
}

const validateFile = (file) => {
    const allowedTypes = [
      'text/plain',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'application/vnd.ms-excel.sheet.macroEnabled.12',
      'message/rfc822',
      'application/vnd.ms-outlook',
      'application/vnd.ms-excel',
      'text/html',
      'application/pdf',
      'text/xml',
      'application/xml',
      'application/csv',
      'text/csv',
      'application/vnd.oasis.opendocument.text', // for 'odt' files
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/rtf',
      'application/epub+zip',
      'application/zip',
      'application/x-zip-compressed',
      'application/json',
      'text/markdown',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'text/tab-separated-values',
      'text/x-c', // for 'c' and 'cc' files
      'text/x-h', // for 'hh' files
      'application/x-httpd-php', // for 'php' files
      'application/javascript', // for 'js' files
      'text/x-python', // for 'py' files
      'application/sql', // for 'sql' files
      'text/x-csharp', // for 'cs' files
      'application/wsdl+xml', // Added for .wsdl
      'application/xsd+xml',
      'text/x-sh',
      'text/javascript',
      'text/php',
      'text/x-python-script',
      'text/rtf',
      'text/x-ruby-script',
      'application/x-sh',
      'application/x-yaml',
      'application/yaml',
      'text/x-scala',
      'text/x-sbt',
      'text/x-go',
      'text/x-c++src', // for .cpp
      'text/x-c++hdr', // for .h
      'application/x-ipynb+json', // for .ipynb
      'text/x-dockerfile', // Added for .dockerfile
      'text/yaml', // for .yaml
      'text/x-java-source', // for .java
      'application/x-ruby', // for .rb
      'application/x-bat', // for .bat
      'application/x-powershell', // for .ps1
      'application/typescript', // Added for .ts
      'text/typescript', // Added for .ts
      'application/typescript-jsx', // Added for .tsx
      'text/typescript-jsx', // Added for .tsx
      'text/x-rustsrc' // Added for .rs
    ];

    const allowedTypesAudio = [
      'audio/mpeg', // mp3
      'video/mp4', // mp4
      'video/mpeg', // mpeg
      'audio/mpeg', // mpga
      'audio/mp4', // m4a
      'audio/x-m4a', // m4a
      'audio/wav', // wav
      'video/webm' // webm
    ]
    const allowedSize = 250 * 1024 * 1024; // 250MB
    const allowedSizeAudio = 500 * 1024 * 1024; // 500MB

    var type = file.type;
    if(type === '') {
      type = 'text/plain'
    }
    console.log(type)
    if(allowedTypesAudio.includes(type)) {
      return allowedTypesAudio.includes(type) && file.size <= allowedSizeAudio;  
    }
    return allowedTypes.includes(type) && file.size <= allowedSize;
  };

// Function to detect the delimiter in the first line of the CSV
function detectDelimiter(line) {
  const delimiters = [',', ';', '\t', '|'];
  for (let delimiter of delimiters) {
    if (line.includes(delimiter)) {
      return delimiter;
    }
  }
  return null;
}

const removeMetadata = (text) => {
  return text.replace(/{"asksage_metadata": {"filename": "audio.mp4", "page_number": "1"}}\n/g, '');
};

const get_loading_message = (theme) => {
  return `<img class=${theme.palette.mode === 'dark' ? "spinner" : 'spinner-dark'} src="spinner.png" alt="Loading..."> I'm thinking, please wait...`;
}

const getLoginLogo = () => {
  if(default_data.default_cobranding_logo !== null && default_data.default_cobranding_logo !== '' && default_data.default_cobranding_logo !== undefined) {
    return '/' + default_data.default_cobranding_logo;
  }
  return '/login-logo.png';
};

export { validateFile, getFileExtension, detectDelimiter, removeMetadata, getLoginLogo, get_loading_message };